import React, { useEffect, useState } from "react";
import { Repeat } from "lucide-react";
import { useAuth } from "@/shared/context/AuthContext";
import { api } from "@/shared/lib/api";

/**
 * DestinationSuggestionChips — frequent-destination quick-tap chips.
 *
 * Visible only when:
 *   • user is signed in
 *   • the destination field is empty (don't clutter while typing)
 *   • backend returned at least one suggestion (rider has 3+ trips to
 *     the same place across 2+ dates in the last 90 days)
 *
 * Tap → fills the destination input via the supplied `onPick(address)`
 * callback. Caller is responsible for the downstream side effects
 * (calc, route-fit, etc.) — this component only mutates the field
 * value so the booking flow remains the single source of truth.
 */
export default function DestinationSuggestionChips({ visible, onPick, testId = "dest-suggestions" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get("/me/destinations/suggested");
        if (!stop) {
          setItems(Array.isArray(data?.suggestions) ? data.suggestions : []);
          setLoaded(true);
        }
      } catch (_) {
        if (!stop) { setItems([]); setLoaded(true); }
      }
    })();
    return () => { stop = true; };
  }, [user?.id]);

  if (!visible || !loaded || items.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testId}>
      {items.map((s, i) => (
        <button
          key={s.address}
          type="button"
          onClick={() => onPick(s.address)}
          className="inline-flex items-center gap-1.5 bg-[#ed6500]/12 hover:bg-[#ed6500]/20 border border-[#ed6500]/30 text-[#F9F7F4] rounded-full pl-2.5 pr-3 py-1.5 text-[11.5px] font-semibold transition-colors max-w-full"
          data-testid={`${testId}-chip-${i}`}
          title={s.address}
        >
          <Repeat size={11} className="text-[#ed6500] shrink-0" />
          <span className="truncate">Continue to {s.label}</span>
        </button>
      ))}
    </div>
  );
}
